import type { ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight, Info, Minus, MoreHorizontal, TrendingDown, TrendingUp } from 'lucide-react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, PolarAngleAxis, PolarGrid, Radar, RadarChart, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts'
import type { Fund } from './types'
import type { FundAnalysis } from './analytics'
import { formatNumber, formatPct, formatTRY, shortNumber } from './analytics'

const chartTooltipStyle = { background: '#111a1f', border: '1px solid #29383d', borderRadius: 10, color: '#ecf4ef', fontSize: 12 }
const chartAxis = { fill: '#6f8380', fontSize: 10 }
const chartGrid = '#1e2b2f'
const palette = ['#e9c46a', '#5ad8b5', '#8ca9ff', '#f08cae', '#a78bfa', '#6bd4ff']

export function Panel({ children, className = '', ...props }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLElement>) {
  return <section className={`panel ${className}`} {...props}>{children}</section>
}

export function SectionHeading({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description?: string; action?: ReactNode }) {
  return <div className="section-heading">
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2>{title}</h2>
      {description && <p className="section-description">{description}</p>}
    </div>
    {action && <div className="section-action">{action}</div>}
  </div>
}

export function ScoreRing({ score, size = 'medium', label, tone = 'mint' }: { score: number; size?: 'small' | 'medium' | 'large'; label?: string; tone?: 'mint' | 'amber' | 'rose' | 'blue' }) {
  return <div className={`score-ring-wrap ${size}`}>
    <div className={`score-ring tone-${tone}`} style={{ '--score': `${Math.round(score) * 3.6}deg` } as React.CSSProperties}>
      <div className="score-ring-inner"><strong>{Math.round(score)}</strong>{label && <span>{label}</span>}</div>
    </div>
  </div>
}

export function MetricCard({ label, value, sub, tone = 'neutral', icon, onInfo, trend }: { label: string; value: string; sub?: string; tone?: 'neutral' | 'positive' | 'negative' | 'amber' | 'blue'; icon?: ReactNode; onInfo?: () => void; trend?: 'up' | 'down' | 'flat' }) {
  return <div className={`metric-card tone-${tone}`}>
    <div className="metric-card-label">{icon}<span>{label}</span>{onInfo && <button className="icon-button mini" onClick={onInfo} aria-label={`About ${label}`}><Info size={13} /></button>}</div>
    <div className="metric-card-value">{value}{trend && <span className={`trend-icon ${trend}`} aria-hidden="true">{trend === 'up' ? <ArrowUpRight size={15} /> : trend === 'down' ? <ArrowDownRight size={15} /> : <Minus size={15} />}</span>}</div>
    {sub && <div className="metric-card-sub">{sub}</div>}
  </div>
}

export function Freshness({ date, label = true }: { date: string; label?: boolean }) {
  const days = Math.max(0, Math.round((new Date().getTime() - new Date(date).getTime()) / 86400000))
  const state = days <= 3 ? 'fresh' : days <= 14 ? 'aging' : 'outdated'
  return <span className={`freshness ${state}`}><i />{label && `${state} · ${days}d`}</span>
}

export function StatusPill({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'positive' | 'negative' | 'amber' | 'blue' | 'neutral' }) {
  return <span className={`status-pill ${tone}`}>{children}</span>
}

export function Tag({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'mint' | 'amber' | 'rose' | 'blue' }) {
  return <span className={`tag ${tone}`}>{children}</span>
}

export function ProgressBar({ value, tone = 'mint', label, right }: { value: number; tone?: 'mint' | 'amber' | 'rose' | 'blue'; label?: string; right?: string }) {
  return <div className="progress-row">
    {(label || right) && <div className="progress-label"><span>{label}</span><span>{right ?? `${Math.round(value)}%`}</span></div>}
    <div className="progress-track"><div className={`progress-fill ${tone}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></div>
  </div>
}

export function TrendMarker({ value, suffix = '%' }: { value: number | null; suffix?: string }) {
  if (value === null) return <span className="muted">—</span>
  return <span className={`trend-marker ${value > 0 ? 'positive' : value < 0 ? 'negative' : 'flat'}`}>{value > 0 ? <TrendingUp size={13} /> : value < 0 ? <TrendingDown size={13} /> : <Minus size={13} />}{formatPct(value, 1).replace('%', '')}{suffix}</span>
}

export function ExposureBars({ items, accent = '#5ad8b5', compact = false }: { items: { id: string; name: string; value: number; detail?: string }[]; accent?: string; compact?: boolean }) {
  return <div className={`exposure-bars ${compact ? 'compact' : ''}`}>
    {items.slice().sort((a, b) => b.value - a.value).map((item, index) => <div className="exposure-bar" key={item.id}>
      <div className="exposure-bar-meta"><span>{item.name}</span><strong>{formatNumber(item.value, 1)}%</strong></div>
      <div className="exposure-track"><div className="exposure-fill" style={{ width: `${Math.min(item.value, 100)}%`, background: index === 0 ? accent : `${accent}bb` }} /></div>
      {item.detail && <small>{item.detail}</small>}
    </div>)}
  </div>
}

export function ExposureDonut({ items, height = 230 }: { items: { id: string; name: string; value: number }[]; height?: number }) {
  const data = items.filter((item) => item.value > 0).map((item) => ({ name: item.name, value: item.value }))
  return <div className="donut-wrap" style={{ height }}>
    <ResponsiveContainer width="100%" height="100%"><PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="84%" paddingAngle={2} stroke="none">
        {data.map((entry, index) => <Cell key={entry.name} fill={palette[index % palette.length]} />)}
      </Pie>
      <ChartTooltip contentStyle={chartTooltipStyle} formatter={(value: number) => [`${value}%`, 'Exposure']} />
    </PieChart></ResponsiveContainer>
    <div className="donut-center"><span>total</span><strong>100%</strong></div>
  </div>
}

export function PerformanceChart({ fund, height = 260 }: { fund: Fund; height?: number }) {
  const periods = ['1m', '3m', '6m', 'ytd', '1y'] as const
  const data = periods.map((period) => ({ period: period.toUpperCase(), fund: fund.returns[period].fund, benchmark: fund.returns[period].benchmark, category: fund.returns[period].category }))
  return <div className="chart-box" style={{ height }}><ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
    <CartesianGrid stroke={chartGrid} vertical={false} />
    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={chartAxis} />
    <YAxis axisLine={false} tickLine={false} tick={chartAxis} tickFormatter={(value) => `${value}%`} />
    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(value: number, name: string) => [`${value?.toFixed(1)}%`, name === 'fund' ? fund.code : name]} />
    <ReferenceLine y={0} stroke="#3b4a4d" />
    <Line type="monotone" dataKey="fund" stroke="#e9c46a" strokeWidth={3} dot={{ r: 3, fill: '#e9c46a', strokeWidth: 0 }} />
    <Line type="monotone" dataKey="benchmark" stroke="#5ad8b5" strokeWidth={1.5} dot={false} strokeDasharray="5 4" />
    <Line type="monotone" dataKey="category" stroke="#8ca9ff" strokeWidth={1.5} dot={false} />
  </LineChart></ResponsiveContainer></div>
}

export function FlowChart({ fund, height = 210 }: { fund: Fund; height?: number }) {
  const data = [{ name: 'Daily', value: fund.cashFlow.daily }, { name: 'Weekly', value: fund.cashFlow.weekly }, { name: 'Monthly', value: fund.cashFlow.monthly }]
  return <div className="chart-box" style={{ height }}><ResponsiveContainer width="100%" height="100%"><BarChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
    <CartesianGrid stroke={chartGrid} vertical={false} />
    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={chartAxis} />
    <YAxis axisLine={false} tickLine={false} tick={chartAxis} tickFormatter={(value) => `${value}%`} />
    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(value: number) => [`${value?.toFixed(1)}%`, 'Net flow']} />
    <ReferenceLine y={0} stroke="#3b4a4d" />
    <Bar dataKey="value" radius={[5, 5, 0, 0]}>{data.map((entry) => <Cell key={entry.name} fill={(entry.value ?? 0) >= 0 ? '#5ad8b5' : '#f08cae'} />)}</Bar>
  </BarChart></ResponsiveContainer></div>
}

export function HistoryChart({ fund, metric = 'size', height = 210 }: { fund: Fund; metric?: 'size' | 'investors'; height?: number }) {
  const source = metric === 'size' ? fund.sizeHistory : fund.investorHistory
  const data = source.map((item) => ({ period: item.period, value: item.value }))
  return <div className="chart-box" style={{ height }}><ResponsiveContainer width="100%" height="100%"><AreaChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
    <defs><linearGradient id={`history-${metric}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8ca9ff" stopOpacity={0.34} /><stop offset="100%" stopColor="#8ca9ff" stopOpacity={0} /></linearGradient></defs>
    <CartesianGrid stroke={chartGrid} vertical={false} />
    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={chartAxis} />
    <YAxis axisLine={false} tickLine={false} tick={chartAxis} tickFormatter={(value) => shortNumber(value)} />
    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(value: number) => [metric === 'size' ? `${value?.toLocaleString()}m TRY` : value?.toLocaleString(), metric === 'size' ? 'Fund size' : 'Investors']} />
    <Area type="monotone" dataKey="value" stroke="#8ca9ff" fill={`url(#history-${metric})`} strokeWidth={2.5} />
  </AreaChart></ResponsiveContainer></div>
}

export function RadarComparison({ funds, analyses, height = 330 }: { funds: Fund[]; analyses: Map<string, FundAnalysis>; height?: number }) {
  const data = [
    { metric: 'Quality', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.qualityScore ?? 0])) },
    { metric: 'Timing', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.entryScore ?? 0])) },
    { metric: 'Momentum', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.momentum ?? 0])) },
    { metric: 'Risk adj.', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.riskAdjustedScore ?? 0])) },
    { metric: 'Manager', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.managerScore ?? 0])) },
    { metric: 'Market', ...Object.fromEntries(funds.map((fund) => [fund.code, analyses.get(fund.id)?.marketScore ?? 0])) },
  ]
  return <div className="chart-box radar-chart" style={{ height }}><ResponsiveContainer width="100%" height="100%"><RadarChart data={data} outerRadius="68%">
    <PolarGrid stroke="#2a3a3e" /><PolarAngleAxis dataKey="metric" tick={{ fill: '#879895', fontSize: 10 }} />
    {funds.map((fund, index) => <Radar key={fund.id} name={fund.code} dataKey={fund.code} stroke={palette[index % palette.length]} fill={palette[index % palette.length]} fillOpacity={0.12} strokeWidth={2} />)}
    <ChartTooltip contentStyle={chartTooltipStyle} />
  </RadarChart></ResponsiveContainer></div>
}

export function ScatterRiskReturn({ funds, analyses, height = 260 }: { funds: Fund[]; analyses: Map<string, FundAnalysis>; height?: number }) {
  const data = funds.map((fund) => ({ x: fund.volatility.y1 ?? 0, y: fund.returns['1y'].fund ?? 0, code: fund.code, z: analyses.get(fund.id)?.qualityScore ?? 50 }))
  return <div className="chart-box" style={{ height }}><ResponsiveContainer width="100%" height="100%"><ScatterChart margin={{ top: 10, right: 12, left: -8, bottom: 2 }}>
    <CartesianGrid stroke={chartGrid} /><XAxis type="number" dataKey="x" name="Volatility" unit="%" tick={chartAxis} axisLine={false} tickLine={false} /><YAxis type="number" dataKey="y" name="1Y return" unit="%" tick={chartAxis} axisLine={false} tickLine={false} />
    <ChartTooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={chartTooltipStyle} formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name === 'x' ? 'Volatility' : '1Y return']} labelFormatter={(_, payload) => payload?.[0]?.payload?.code ?? ''} />
    <Scatter data={data} fill="#e9c46a" shape="circle" />
  </ScatterChart></ResponsiveContainer></div>
}

export function CorrelationHeatmap({ correlations }: { correlations: Record<string, number | null> }) {
  const entries = Object.entries(correlations)
  return <div className="correlation-grid">{entries.map(([name, value]) => <div className="correlation-cell" key={name}>
    <div className="correlation-value" style={{ background: value === null ? '#1c292d' : value >= 0 ? `rgba(90,216,181,${Math.min(Math.abs(value) * 0.82, 0.82)})` : `rgba(240,140,174,${Math.min(Math.abs(value) * 0.82, 0.82)})` }}>{value === null ? '—' : value.toFixed(2)}</div>
    <span>{name}</span>
  </div>)}</div>
}

export function InfoButton({ onClick, label = 'Explain this metric' }: { onClick: () => void; label?: string }) {
  return <button className="icon-button mini" onClick={onClick} aria-label={label}><Info size={13} /></button>
}

export function MoreButton() {
  return <button className="icon-button" aria-label="More options"><MoreHorizontal size={16} /></button>
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><div className="empty-state-mark">◌</div><h3>{title}</h3><p>{description}</p>{action}</div>
}

export function NumberTable({ rows, headers }: { rows: (string | ReactNode)[][]; headers: string[] }) {
  return <div className="table-wrap"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>
}

export function FundMiniIdentity({ fund, analysis }: { fund: Fund; analysis?: FundAnalysis }) {
  return <div className="mini-identity"><div className="fund-avatar">{fund.code.slice(0, 2)}</div><div><strong>{fund.code}</strong><span>{fund.name}</span></div>{analysis && <StatusPill tone={analysis.recommendationLevel === 'positive' ? 'positive' : analysis.recommendationLevel === 'negative' ? 'negative' : 'amber'}>{analysis.recommendation}</StatusPill>}</div>
}

export function ChartLegend({ items }: { items: { label: string; color: string }[] }) {
  return <div className="chart-legend">{items.map((item) => <span key={item.label}><i style={{ background: item.color }} />{item.label}</span>)}</div>
}

export function MicroStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return <div className="micro-stat"><span>{label}</span><strong>{value}</strong>{sub && <small>{sub}</small>}</div>
}

export function PositionValue({ amount, price, averagePrice }: { amount: number; price: number; averagePrice: number }) {
  const value = amount * price / averagePrice
  const pnl = value - amount
  const pct = amount ? (pnl / amount) * 100 : 0
  return <div className="position-value"><strong>{formatTRY(value)}</strong><span className={pnl >= 0 ? 'positive-text' : 'negative-text'}>{formatTRY(pnl)} · {formatPct(pct)}</span></div>
}
